import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faCoins,
  faClock,
  faMoneyBillWave,
  faShieldAlt,
  faSearch,
  faCalendarAlt,
  faArrowDown,
  faArrowUp,
  faCheckCircle,
  faHourglassHalf,
  faTimesCircle,
  faChevronRight,
  faChevronLeft,
  faSackDollar,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Card,
  Table,
  Badge,
  Form,
  InputGroup,
  Button,
} from "@themesberg/react-bootstrap";

import { userWalletData, updateBufferAmount } from "../../../pages/api";

const formatCurrency = (value) => {
  const num = Number(value || 0);
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getStatusBadge = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "success":
    case "paid":
    case "credit":
    case "completed":
      return "success";
    case "pending":
      return "warning";
    case "failed":
    case "debit":
      return "danger";
    default:
      return "secondary";
  }
};

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const mapWalletTransaction = (item) => ({
  id: item?.id ?? Date.now(),
  userId: item?.user_id ?? "N/A",
  type: String(item?.type || "N/A" || "-"),
  amount: Number(item?.amount || 0),
  description: item?.description || "N/A" || "-",
  paymentMethod: item?.payment_method || "N/A" || "-",
  status: item?.status || "N/A" || "-",
});

export default function UserWalletPage() {
  const [loading, setLoading] = useState(false);
  const [savingBuffer, setSavingBuffer] = useState(false);
  const [search, setSearch] = useState("");
  const [reservedBufferAmount, setReservedBufferAmount] = useState("0");
  const [summary, setSummary] = useState({
    total_earnings: 0,
    available_balance: 0,
    pending_earnings: 0,
    total_withdrawn: 0,
    reserved_buffer_amount: 0,
    total_bonus_amount: 0,
    wallet_balance: 0,
  });
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const searchDebounceRef = useRef(null);
  const firstSearchRenderRef = useRef(true);

  const fetchWalletData = async (page = 1, perPage = 10, searchTerm = "") => {
    try {
      setLoading(true);

      const response = await userWalletData({
        page,
        per_page: perPage,
        search: searchTerm,
      });

      const data = response?.data?.data || {};
      const nextSummary = data?.summary || {};
      const apiTransactions = Array.isArray(data?.transactions)
        ? data.transactions
        : [];
      const apiPagination = data?.pagination || {};

      setSummary({
        total_earnings: Number(nextSummary?.total_earnings || 0),
        available_balance: Number(nextSummary?.available_balance || 0),
        pending_earnings: Number(nextSummary?.pending_earnings || 0),
        total_withdrawn: Number(nextSummary?.total_withdrawn || 0),
        reserved_buffer_amount: Number(nextSummary?.reserved_buffer_amount || 0),
        wallet_balance: Number(nextSummary?.wallet_balance || 0),
        total_bonus_amount: Number(nextSummary?.total_bonus_amount || 0),
      });

      setReservedBufferAmount(
        String(Number(nextSummary?.reserved_buffer_amount || 0))
      );

      setTransactions(apiTransactions.map(mapWalletTransaction));
      setPagination({
        current_page: Number(apiPagination.current_page ?? page) || 1,
        last_page: Number(apiPagination.last_page ?? 1) || 1,
        per_page: Number(apiPagination.per_page ?? perPage) || perPage,
        total:
          Number(apiPagination.total ?? apiTransactions.length) ||
          apiTransactions.length,
      });
    } catch (error) {
      console.error("Failed to fetch wallet data:", error);
      toast.error(error?.response?.data?.message || "Unable to load wallet data.");
      setTransactions([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData(1, pagination.per_page, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstSearchRenderRef.current) {
      firstSearchRenderRef.current = false;
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      fetchWalletData(1, pagination.per_page, search.trim());
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const handleBufferUpdate = async () => {
    const amountValue = Number(reservedBufferAmount || 0);

    if (!reservedBufferAmount || amountValue <= 0) {
      toast.error("Please enter a valid buffer amount.");
      return;
    }

    try {
      setSavingBuffer(true);

      const response = await updateBufferAmount({
        buffer_amount: amountValue,
      });

      toast.success(
        response?.data?.message || "Buffer amount updated successfully."
      );

      await fetchWalletData(1, pagination.per_page, search.trim());
    } catch (error) {
      console.error("Failed to update buffer amount:", error);
      toast.error(
        error?.response?.data?.message || "Unable to update buffer amount."
      );
    } finally {
      setSavingBuffer(false);
    }
  };

  const totalReservedBuffer = Number(reservedBufferAmount || 0);

  const getShowingRange = () => {
    const total = pagination.total || 0;
    const current = pagination.current_page || 1;
    const perPage = pagination.per_page || 10;

    if (total === 0) {
      return { start: 0, end: 0 };
    }

    const start = (current - 1) * perPage + 1;
    const end = Math.min(current * perPage, total);

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Start pages
    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    // End pages
    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    // Middle pages: 1 ... previous current next ... last
    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = getShowingRange();

  return (
    <>
      <div className="lsg-page">
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-2 mb-3">
        <div>
          <h2 className="lsg-page-title">My Wallet</h2>
          <p className="lsg-page-subtitle">Track your earnings, balance, and withdrawals.</p>
        </div>
      </div>

      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Earnings</div>
                  <h3 className="mb-0">
                    {loading ? "..." : formatCurrency(summary.total_earnings)}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faCoins} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Available Balance</div>
                  <h3 className="mb-0">
                    {loading ? "..." : formatCurrency(summary.available_balance)}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faWallet} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Pending Earnings</div>
                  <h3 className="mb-0">
                    {loading ? "..." : formatCurrency(summary.pending_earnings)}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faHourglassHalf} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* <Col xs={12} sm={6} xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Withdrawn</div>
                  <h3 className="mb-0">
                    {loading ? "..." : formatCurrency(summary.total_withdrawn)}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faArrowDown} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col> */}

        {/* <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Bonus Amount</div>
                  <h3 className="mb-0">
                    {loading ? "..." : formatCurrency(summary.total_bonus_amount)}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faSackDollar} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>


        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Wallet Balance</div>
                  <h3 className="mb-0">
                    {loading ? "..." : formatCurrency(summary.wallet_balance)}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faMoneyBillWave} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col> */}

        <Col xs={12} sm={12} xl={12}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">
                    Reserved Buffer Amount
                  </div>
                  <h3 className="mb-0">{formatCurrency(summary.reserved_buffer_amount)}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faShieldAlt} />
                </div>
              </div>

              <div className="mt-4">
                <Form.Group>
                  <Form.Label className="fw-semibold">Set Buffer Amount</Form.Label>
                  <InputGroup>
                    <InputGroup.Text className="bg-white">₹</InputGroup.Text>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      placeholder="Enter buffer amount"
                      value={reservedBufferAmount}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, "");
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          setReservedBufferAmount(value);
                        }
                      }}
                      style={{ boxShadow: "none" }}
                    />
                    <Button
                      type="button"
                      className="act-btn"
                      onClick={handleBufferUpdate}
                      disabled={loading || savingBuffer}
                    >
                      {savingBuffer ? "Saving..." : "Set Buffer"}
                    </Button>
                  </InputGroup>
                </Form.Group>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* <Col xs={12} sm={6} xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Wallet Activity</div>
                  <h3 className="mb-0">Live</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    background: "rgba(44, 96, 186, 0.1)",
                    color: "#1D4E89",
                  }}
                >
                  <FontAwesomeIcon icon={faClock} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col> */}
      </Row>

      <Row className="g-4 mb-4">
        {/* <Col xs={12} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <h5 className="mb-3">Preferred Buffer Wallet Amount</h5>
              <Form.Group>
                <Form.Label className="fw-semibold">Amount to retain</Form.Label>
                <InputGroup>
                  <InputGroup.Text className="bg-white">₹</InputGroup.Text>
                  <Form.Control
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter buffer amount"
                    value={reservedBufferAmount}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^\d.]/g, "");
                      if (value === "" || /^\d*\.?\d*$/.test(value)) {
                        setReservedBufferAmount(value);
                      }
                    }}
                    style={{ boxShadow: "none" }}
                  />
                </InputGroup>
                <Form.Text className="text-muted">
                  This buffer is reserved from wallet balance.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>
        </Col> */}

        <Col xs={12} xl={12}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <h5 className="mb-1">Transactions</h5>
                  {/* <div className="text-muted small">
                    Date / Type / Payment Method / Amount
                  </div> */}
                </div>

                <InputGroup style={{ maxWidth: "320px" }}>
                  <InputGroup.Text className="bg-white">
                    <FontAwesomeIcon icon={faSearch} className="text-muted" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ boxShadow: "none" }}
                  />
                </InputGroup>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Type</th>
                      <th>Payment Method</th>
                      <th className="text-end">Amount</th>
                      <th className="text-center">Description</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-5">
                          Loading transactions...
                        </td>
                      </tr>
                    ) : transactions.length > 0 ? (
                      transactions.map((item, index) => {
                        const isDebit =
                          String(item.type || "").toLowerCase() === "debit";
                        const signedAmount = isDebit
                          ? -Math.abs(item.amount)
                          : Math.abs(item.amount);

                        return (
                          <tr key={item.id}>
                            <td className="fw-semibold">{index + 1}</td>
                            <td>
                              <Badge
                                bg={getStatusBadge(item.type)}
                                className="px-3 py-2 rounded-pill text-capitalize"
                              >
                                {capitalize(item.type)}
                              </Badge>
                            </td>
                            <td>{capitalize(item.paymentMethod)}</td>
                            <td
                              className={`text-end fw-bold ${item.type === "credit"
                                  ? "text-success"
                                  : "text-danger"
                                }`}
                            >
                              {item.type === "credit" ? "+" : "-"}
                              {formatCurrency(Math.abs(signedAmount))}
                            </td>
                            <td className="text-center">{item.description}</td>
                            <td className="text-center">
                              <Badge bg={getStatusBadge(item.status)} className="px-3 py-2 rounded-pill">
                                {capitalize(item.status)}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-5">
                          No transactions found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted small">
                  Showing {start} to {end} of {pagination.total} entries
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={pagination.current_page <= 1 || loading}
                    onClick={() =>
                      fetchWalletData(
                        Math.max(1, pagination.current_page - 1),
                        pagination.per_page,
                        search.trim()
                      )
                    }
                    className="course-page-btn"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Button>

                  {getPageItems().map((item, index) =>
                    item === "..." ? (
                      <span key={`dots-${index}`} className="text-muted px-1">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={item}
                        type="button"
                        variant="outline-primary"
                        disabled={loading}
                        onClick={() =>
                          fetchWalletData(
                            item,
                            pagination.per_page,
                            search.trim()
                          )
                        }
                        className={`course-page-btn ${item === pagination.current_page
                            ? "course-page-btn-active"
                            : ""
                          }`}
                      >
                        {item}
                      </Button>
                    )
                  )}

                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={
                      pagination.current_page >= pagination.last_page || loading
                    }
                    onClick={() =>
                      fetchWalletData(
                        Math.min(
                          pagination.last_page,
                          pagination.current_page + 1
                        ),
                        pagination.per_page,
                        search.trim()
                      )
                    }
                    className="course-page-btn"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </div>
    </>
  );
}